import Layout from '@/components/feature/Layout';
import FaqAccordion from '@/components/feature/FaqAccordion';
import { PageBreadcrumb, RelatedLinks, ctaSidebar } from '@/pages/shared/PageHelpers';
import { products } from '@/mocks/products';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Soldagem de Geomembrana: Técnicas, Máquinas e Cuidados',
  description:
    'Guia técnico completo sobre soldagem de geomembrana: preparação, temperatura, velocidade, pressão, teste de solda, inspeção e erros comuns.',
  url: 'https://maquinadesoldageomembrana.com.br/soldagem-de-geomembrana',
  inLanguage: 'pt-BR',
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Quais são os principais métodos de soldagem de geomembrana?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Os dois métodos principais são a solda por cunha quente (hot wedge), que funde as duas folhas simultaneamente criando uma solda dupla com canal de teste, e a solda por extrusão, que aplica fio de PEAD fundido sobre a emenda para cantos, junções e reparos.',
      },
    },
    {
      '@type': 'Question',
      name: 'Qual a temperatura ideal para soldar geomembrana PEAD?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A temperatura ideal varia entre 280°C e 380°C na cunha, dependendo da espessura do material, velocidade de soldagem e condições ambientais. Equipamentos como o BT950 e o CX500L permitem ajuste fino de 0 a 450°C.',
      },
    },
    {
      '@type': 'Question',
      name: 'O que é solda dupla com canal de teste de pressão?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A solda dupla cria duas linhas de fusão paralelas separadas por um canal central vazio. Esse canal permite realizar teste de pressão de ar, comprovando a estanqueidade da emenda, conforme exigido pela norma NBR 16199.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quais erros comuns comprometem a qualidade da solda?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Os erros mais comuns são: superfície suja ou úmida, sobreposição insuficiente, temperatura inadequada, velocidade excessiva e falta de solda de teste. Todos podem gerar emendas frágeis e vazamentos.',
      },
    },
  ],
};

const faq = [
  {
    question: 'Quais são os principais métodos de soldagem de geomembrana?',
    answer:
      'Os dois métodos principais são a solda por cunha quente (hot wedge), que funde as duas folhas simultaneamente criando uma solda dupla com canal de teste, e a solda por extrusão, que aplica fio de PEAD fundido sobre a emenda para cantos, junções e reparos.',
  },
  {
    question: 'Qual a temperatura ideal para soldar geomembrana PEAD?',
    answer:
      'A temperatura ideal varia entre 280°C e 380°C na cunha, dependendo da espessura do material, velocidade de soldagem e condições ambientais. Equipamentos como o BT950 e o CX500L permitem ajuste fino de 0 a 450°C.',
  },
  {
    question: 'O que é solda dupla com canal de teste de pressão?',
    answer:
      'A solda dupla cria duas linhas de fusão paralelas separadas por um canal central vazio. Esse canal permite realizar teste de pressão de ar, comprovando a estanqueidade da emenda, conforme exigido pela norma NBR 16199.',
  },
  {
    question: 'Quais erros comuns comprometem a qualidade da solda?',
    answer:
      'Os erros mais comuns são: superfície suja ou úmida, sobreposição insuficiente, temperatura inadequada, velocidade excessiva e falta de solda de teste. Todos podem gerar emendas frágeis e vazamentos.',
  },
];

export default function SoldagemDeGeomembranaPage() {
  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <div className="w-full px-4 md:px-6 lg:px-10 pt-28 md:pt-32 pb-16 md:pb-24">
        <PageBreadcrumb items={[{ label: 'Soldagem de Geomembrana' }]} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
          <div className="lg:col-span-2">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 uppercase leading-tight mb-6">
              Soldagem de Geomembrana: Técnicas, Máquinas e Cuidados
            </h1>

            <div className="space-y-5 text-foreground-600 leading-relaxed">
              <p>
                A <strong className="text-foreground-950">soldagem de geomembrana</strong> é a etapa mais crítica de qualquer sistema de impermeabilização. Uma emenda mal executada compromete a estanqueidade de toda a obra, independentemente da qualidade da manta. Por isso, dominar as técnicas, os parâmetros e os cuidados é essencial para garantir um resultado duradouro.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">1. Preparação da Superfície</h2>
              <p>
                Antes de iniciar a solda, a superfície de apoio deve estar nivelada, compactada e livre de pedras, galhos e objetos pontiagudos que possam danificar a geomembrana. O terreno deve ser inspecionado e aprovado por um responsável técnico, pois qualquer irregularidade sob a manta pode gerar furos durante a operação.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">2. Limpeza das Folhas</h2>
              <p>
                As áreas a serem soldadas devem estar completamente limpas e secas. Remova poeira, areia, umidade e qualquer resíduo com pano limpo ou solvente compatível. A presença de contaminantes é uma das principais causas de falha na fusão, pois impede o contato direto entre os polímeros.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">3. Sobreposição (Overlap)</h2>
              <p>
                A sobreposição entre as folhas deve seguir a especificação do projeto, geralmente entre <strong>7 e 15 cm</strong>. Uma sobreposição insuficiente reduz a área de fusão e enfraquece a emenda, enquanto uma sobreposição excessiva desperdiça material e dificulta a inspeção.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-6">
                <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 text-center">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-full mx-auto mb-3">
                    <i className="ri-temp-hot-line text-primary-600" />
                  </div>
                  <h3 className="text-sm font-bold text-foreground-900 uppercase mb-1">Temperatura</h3>
                  <p className="text-foreground-500 text-xs leading-relaxed">280°C a 380°C na cunha para PEAD, conforme espessura.</p>
                </div>
                <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 text-center">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-full mx-auto mb-3">
                    <i className="ri-speed-up-line text-accent-600" />
                  </div>
                  <h3 className="text-sm font-bold text-foreground-900 uppercase mb-1">Velocidade</h3>
                  <p className="text-foreground-500 text-xs leading-relaxed">Controla o tempo de aquecimento do polímero.</p>
                </div>
                <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 text-center">
                  <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-full mx-auto mb-3">
                    <i className="ri-dashboard-line text-secondary-700" />
                  </div>
                  <h3 className="text-sm font-bold text-foreground-900 uppercase mb-1">Pressão</h3>
                  <p className="text-foreground-500 text-xs leading-relaxed">Aplicada pelos rolos para unir as folhas fundidas.</p>
                </div>
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">4. Teste de Solda (Test Weld)</h2>
              <p>
                Antes de iniciar a produção, realize sempre uma <strong>solda de teste</strong> em um retalho do mesmo material, nas mesmas condições de campo. O corpo de prova deve ser testado por tração (peel test e shear test) para confirmar que a fusão ocorreu de forma adequada. Esse procedimento deve ser repetido ao longo do dia e sempre que houver mudança de material ou condições climáticas.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">5. Inspeção e Controle de Qualidade</h2>
              <ul className="space-y-2">
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Inspeção visual</strong> — verificar continuidade, alinhamento e ausência de dobras ou bolhas.</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Teste de pressão de ar</strong> — aplicado no canal da solda dupla para comprovar estanqueidade.</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Teste de vácuo</strong> — indicado para soldas por extrusão e pontos de reparo.</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Registro documental</strong> — manter o histórico de soldas, parâmetros e testes realizados.</span></li>
              </ul>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">6. Erros Comuns a Evitar</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-4">
                {[
                  { t: 'Superfície suja ou úmida', d: 'Contaminantes impedem a fusão correta dos polímeros.' },
                  { t: 'Temperatura inadequada', d: 'Muito baixa não funde; muito alta degrada o material.' },
                  { t: 'Velocidade excessiva', d: 'Reduz o tempo de aquecimento e enfraquece a emenda.' },
                  { t: 'Falta de solda de teste', d: 'Sem teste não há como validar os parâmetros do dia.' },
                ].map((e) => (
                  <div key={e.t} className="flex items-start gap-3 bg-background-100 rounded-xl border border-background-200/70 p-4">
                    <i className="ri-error-warning-line text-accent-500 text-lg flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-foreground-900 text-sm font-bold">{e.t}</p>
                      <p className="text-foreground-500 text-xs leading-relaxed">{e.d}</p>
                    </div>
                  </div>
                ))}
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Equipamentos para Soldagem</h2>
              <p>
                A escolha do equipamento depende do método de soldagem e do tipo de projeto. Para grandes áreas planas, a <strong>cunha quente automática</strong> é a mais produtiva. Para cantos, junções e reparos, a <strong>extrusora</strong> é indispensável. Confira os modelos disponíveis:
              </p>
              <div className="flex flex-wrap gap-2 my-4">
                {products.map((p) => (
                  <span key={p.id} className="inline-flex items-center gap-1 text-xs font-medium bg-background-100 text-foreground-700 px-3 py-1.5 rounded-full">
                    {p.name}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mb-5">Perguntas Frequentes</h2>
              <FaqAccordion items={faq} />
            </div>

            <RelatedLinks
              links={[
                { to: '/como-soldar-geomembrana', label: 'Como Soldar Geomembrana' },
                { to: '/maquina-de-solda-de-geomembrana', label: 'Máquina de Solda de Geomembrana' },
                { to: '/cunha-quente-geomembrana', label: 'Cunha Quente' },
                { to: '/extrusora-geomembrana', label: 'Extrusora' },
                { to: '/modelos-de-maquinas', label: 'Comparativo de Modelos' },
              ]}
            />
          </div>

          <div className="space-y-6">{ctaSidebar}</div>
        </div>
      </div>
    </Layout>
  );
}